import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { GooglePlacesControlledInput } from '@/components/shared/GooglePlacesInput';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type {
  AvailabilityFormValues,
  AvailabilityFormSlotInput,
  AvailabilityWorkModel,
  AvailabilityCandidate,
  CapabilityScoreDebug,
  CandidateScoreDebug,
} from '../../models';
import {
  AvailabilityWorkModels,
  AVAILABILITY_WORK_MODEL_METADATA,
  type AvailabilityCapability,
} from '../../models';
import type { AllocationBucketType } from '@/features/Collaborators/Models';
import { AllocationBucketTypes } from '@/features/Collaborators/Models';
import { ALLOCATION_BUCKET_ICON_MAP } from '@/features/Collaborators/Allocations/constants/allocationTypeIcons';
import clsx from 'clsx';
import { WORK_MODEL_ICON_MAP } from '../../constants/workModelIcons';
import { useResourceOptions } from '@/hooks/useResourceOptions';
import type { ResourceType, Technology, Seniority } from '@/hooks/useResourceOptions';
import { ResourceTypeList, TechnologyList } from '@/components/shared/ResourceCapabilitySelector';
import { CapabilitySearchInput } from '@/components/shared';
import { CollaboratorCombobox } from '@/components/shared';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { ArrowDownRightIcon, ArrowUpRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { UserCircleIcon, SparklesIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/hooks/useToast';
import { useRunSlotMatchMutation } from '../../data';
import { useCapabilitySearch } from '@/hooks/useCapabilitySearch';
import type { CollaboratorPublic } from '@/features/Collaborators/Data';
import { normalizeString } from '@/utils/stringUtils';

const MONTHLY_FULL_LOAD = 168;

const SCORE_WEIGHTS = {
  financial: 30,
  availability: 30,
  location: 10,
  capabilities: 30,
};

const SCORE_SEGMENTS = [
  { key: 'financial' as const, color: '#6366F1', icon: '💰' },
  { key: 'availability' as const, color: '#10B981', icon: '⏱️' },
  { key: 'location' as const, color: '#F59E0B', icon: '📍' },
  { key: 'capabilities' as const, color: '#EC4899', icon: '🧠' },
];

const DEFAULT_SCORE_CUTOFF = 80;
const TAB_OPTIONS = [
  { key: 'vaga', label: 'Dados da Vaga' },
  { key: 'habilidades', label: 'Habilidades' },
  { key: 'colaboradores', label: 'Colaboradores' },
] as const;

type TabKey = (typeof TAB_OPTIONS)[number]['key'];

const FULFILLMENT_STRATEGY_OPTIONS = [
  {
    key: 'known' as const,
    label: 'Eu já sei quem quero alocar',
    description: 'Selecione um colaborador já disponível na equipe.',
    icon: UserCircleIcon,
  },
  {
    key: 'assist' as const,
    label: 'Quero ajuda para encontrar alguém',
    description: 'Utilize a busca inteligente para encontrar correspondências.',
    icon: SparklesIcon,
  },
  {
    key: 'hire' as const,
    label: 'Vou contratar alguém',
    description: 'A vaga será preenchida com um novo colaborador.',
    icon: BriefcaseIcon,
  },
];

const SEGMENT_FILTER_THRESHOLDS = {
  financial: 30,
  availability: 15,
  location: 10,
  capabilities: 20,
} as const;

const SEGMENT_FILTER_LABELS: Record<keyof typeof SEGMENT_FILTER_THRESHOLDS, string> = {
  financial: 'Financeiro',
  availability: 'Disponibilidade',
  location: 'Localização',
  capabilities: 'Capacidades',
};

const SEGMENT_FILTER_KEYS = Object.keys(SEGMENT_FILTER_THRESHOLDS) as Array<keyof typeof SEGMENT_FILTER_THRESHOLDS>;
const SEGMENT_FILTER_ORDER: Array<keyof typeof SEGMENT_FILTER_THRESHOLDS> = [
  'availability',
  'location',
  'financial',
  'capabilities',
];

const SEGMENT_FILTER_STYLES: Record<keyof typeof SEGMENT_FILTER_THRESHOLDS, { active: string; inactive: string; toggle: string }> = {
  financial: {
    active: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    inactive: 'border-indigo-100 bg-white text-indigo-600 hover:bg-indigo-50',
    toggle: 'accent-indigo-600',
  },
  availability: {
    active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    inactive: 'border-emerald-100 bg-white text-emerald-600 hover:bg-emerald-50',
    toggle: 'accent-emerald-600',
  },
  location: {
    active: 'border-amber-200 bg-amber-50 text-amber-700',
    inactive: 'border-amber-100 bg-white text-amber-600 hover:bg-amber-50',
    toggle: 'accent-amber-500',
  },
  capabilities: {
    active: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700',
    inactive: 'border-fuchsia-100 bg-white text-fuchsia-600 hover:bg-fuchsia-50',
    toggle: 'accent-fuchsia-600',
  },
};

type ScoreSegmentKey = keyof typeof SCORE_WEIGHTS;

const SCORE_SEGMENT_MAP = SCORE_SEGMENTS.reduce<Record<ScoreSegmentKey, (typeof SCORE_SEGMENTS)[number]>>(
  (acc, segment) => {
    acc[segment.key] = segment;
    return acc;
  },
  {} as Record<ScoreSegmentKey, (typeof SCORE_SEGMENTS)[number]>,
);

interface ScoreLineProps {
  segmentKey: ScoreSegmentKey;
  score: number | null | undefined;
}

function ScoreLine({ segmentKey, score }: ScoreLineProps) {
  const config = SCORE_SEGMENT_MAP[segmentKey];
  const weight = SCORE_WEIGHTS[segmentKey];
  const numericScore = typeof score === 'number' && Number.isFinite(score) ? score : 0;
  const clampedScore = Math.max(0, Math.min(numericScore, weight));
  const percent = weight === 0 ? 0 : Math.round((clampedScore / weight) * 100);

  return (
    <div className="flex items-center gap-2">
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] text-white shadow-sm"
        style={{ backgroundColor: config.color }}
      >
        {config.icon}
      </span>
      <div className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-gray-200">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            width: `${percent}%`,
            backgroundColor: config.color,
          }}
        />
      </div>
      <span className="w-7 text-right text-xs font-semibold text-gray-700">{Math.round(clampedScore)}</span>
    </div>
  );
}

interface AvailabilitySlotEditorProps {
  open: boolean;
  slotIndex: number | null;
  onClose: () => void;
  bucketMetadata: Record<AllocationBucketType, { label: string; description: string }>;
  availabilityId?: string;
}

export function AvailabilitySlotEditor({
  open,
  slotIndex,
  onClose,
  bucketMetadata,
  availabilityId,
}: AvailabilitySlotEditorProps) {
  const { info, success, error } = useToast();
  const runMatchMutation = useRunSlotMatchMutation();

  const {
    types,
    technologies,
    seniorities,
    loading: resourcesLoading,
    error: resourcesError,
  } = useResourceOptions();

  const [capabilitySelection, setCapabilitySelection] = useState({
    typeId: '',
    typeName: '',
    technologyId: '',
    technologyName: '',
    seniorityId: '',
    seniorityName: '',
    required: true,
  });
  const [capabilityError, setCapabilityError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const formContext = useFormContext<AvailabilityFormValues>();
  const { register, control, watch, setValue, formState, getValues } = formContext;

  const resolvedSlotIndex = slotIndex ?? 0;
  const slotPath = `slots.${resolvedSlotIndex}` as const;
  const slotErrors =
    ((formState.errors?.slots as Record<number, Record<string, unknown>> | undefined) ?? {})[
    resolvedSlotIndex
    ] ?? {};

  const slot = watch(slotPath) as AvailabilityFormSlotInput | undefined;
  const slotId = slot?.slotId ? String(slot.slotId) : `transient-slot-${resolvedSlotIndex}`;
  const chosenCollaboratorId = slot?.chosenCollaboratorId ?? null;
  const fulfillmentStrategy = slot?.fulfillmentStrategy ?? 'known';
  const currentBucketType = slot?.bucketType ?? AllocationBucketTypes.Daily;
  const referenceDateRaw = watch('referenceDate') as Date | string | null;
  const referenceDateValue = useMemo(() => {
    if (!referenceDateRaw) {
      return null;
    }
    if (referenceDateRaw instanceof Date) {
      return referenceDateRaw;
    }
    const parsed = new Date(referenceDateRaw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [referenceDateRaw]);

  useEffect(() => {
    if (!slot?.bucketType) {
      setValue(`${slotPath}.bucketType`, AllocationBucketTypes.Monthly, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
    if (slot?.bucketValue == null) {
      setValue(`${slotPath}.bucketValue`, 168, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [slot?.bucketType, slot?.bucketValue, setValue, slotPath]);

  useEffect(() => {
    if (!slot?.fulfillmentStrategy) {
      setValue(`${slotPath}.fulfillmentStrategy`, 'known', {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [slot?.fulfillmentStrategy, setValue, slotPath]);

  useEffect(() => {
    const raw = slot?.lastSuggestionAt as { toDate?: () => Date } | undefined;
    if (raw?.toDate) {
      setLastUpdatedAt(raw.toDate());
    }
  }, [slot?.lastSuggestionAt]);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return null;
    return formatDistanceToNow(lastUpdatedAt, { addSuffix: true, locale: ptBR });
  }, [lastUpdatedAt]);

  type CandidateWithMatch = AvailabilityCandidate & {
    matchScore?: number;
    matchScores?: {
      financial: number;
      availability: number;
      location: number;
      capabilities: number;
    };
    matchAssumptions?: {
      salaryMissing: boolean;
      salaryLimitMissing: boolean;
      availabilityMissing: boolean;
      locationFallback: boolean;
      optionalAutoFulfilled: boolean;
    };
    matchInputs?: Record<string, unknown>;
    matchContractType?: string | null;
    matchNormalizedSalary?: number | null;
    matchLocationCity?: string | null;
    matchLocationState?: string | null;
    capabilityDebug?: CapabilityScoreDebug;
    debug?: CandidateScoreDebug;
  };

  const slotCandidates: CandidateWithMatch[] = (slot?.candidates ?? []) as CandidateWithMatch[];
  const [suggestionResults, setSuggestionResults] = useState<CandidateWithMatch[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>('vaga');
  const [expandedDebugCandidateId, setExpandedDebugCandidateId] = useState<string | null>(null);
  const [suggestionMeta, setSuggestionMeta] = useState<{ total: number; page: number; pageSize: number; totalPages: number } | null>(null);
  const [scoreCutoff, setScoreCutoff] = useState<number>(DEFAULT_SCORE_CUTOFF);
  const [segmentFiltersEnabled, setSegmentFiltersEnabled] = useState<Record<keyof typeof SEGMENT_FILTER_THRESHOLDS, boolean>>({
    financial: false,
    availability: false,
    location: false,
    capabilities: false,
  });
  const [collaboratorSearchTerm, setCollaboratorSearchTerm] = useState<string>('');

  const selectedCandidateMap = useMemo(() => {
    const map = new Map<string, CandidateWithMatch>();
    slotCandidates.forEach((candidate) => {
      if (candidate?.collaboratorId) {
        map.set(candidate.collaboratorId, candidate);
      }
    });
    return map;
  }, [slotCandidates]);

  const mergedSuggestions = useMemo(() => {
    return suggestionResults
      .map((candidate) => ({
        ...candidate,
        isChosen: selectedCandidateMap.has(candidate.collaboratorId),
      }))
      .sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0));
  }, [suggestionResults, selectedCandidateMap]);

  const filteredCandidates = useMemo(() => {
    const normalizedQuery = normalizeString(collaboratorSearchTerm);
    if (!normalizedQuery) {
      return mergedSuggestions;
    }
    return mergedSuggestions.filter((candidate) =>
      normalizeString(candidate.collaboratorName).includes(normalizedQuery)
    );
  }, [collaboratorSearchTerm, mergedSuggestions]);

  const selectedCandidatesList = useMemo(() => {
    const withLatestData = slotCandidates
      .filter((candidate) => candidate.isChosen !== false)
      .map((candidate) => {
        const latest = suggestionResults.find((suggestion) => suggestion.collaboratorId === candidate.collaboratorId);
        return latest ? { ...latest, isChosen: true } : candidate;
      });
    return withLatestData.sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0));
  }, [slotCandidates, suggestionResults]);

  const chosenCandidate = useMemo(() => {
    if (!chosenCollaboratorId) {
      return null;
    }
    return (
      selectedCandidatesList.find((candidate) => candidate.collaboratorId === chosenCollaboratorId) ??
      slotCandidates.find((candidate) => candidate.collaboratorId === chosenCollaboratorId) ??
      null
    );
  }, [chosenCollaboratorId, selectedCandidatesList, slotCandidates]);

  const totalSuggestionsCount = suggestionMeta?.total ?? suggestionResults.length;

  const isSuggesting = runMatchMutation.isPending;
  const isSuggestDisabled = isSuggesting;
  const isSuggestionsBusy = isSuggesting;

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }),
    []
  );

  const handleToggleCandidateSelection = useCallback(
    (candidate: CandidateWithMatch) => {
      if (!candidate?.collaboratorId) {
        return;
      }
      const isSelected = slotCandidates.some((item) => item.collaboratorId === candidate.collaboratorId);

      if (isSelected) {
        const updated = slotCandidates.filter((item) => item.collaboratorId !== candidate.collaboratorId);
        setValue(`${slotPath}.candidates`, updated, {
          shouldDirty: true,
          shouldTouch: true,
        });
        if (chosenCollaboratorId === candidate.collaboratorId) {
          setValue(`${slotPath}.chosenCollaboratorId`, null, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      } else {
        const newEntry: CandidateWithMatch = {
          ...candidate,
          isChosen: true,
          addedAt: new Date(),
        };
        const updated = [...slotCandidates, newEntry];
        setValue(`${slotPath}.candidates`, updated, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [chosenCollaboratorId, setValue, slotCandidates, slotPath]
  );

  const handleToggleChosenCandidate = useCallback(
    (candidateId: string) => {
      if (!candidateId) {
        return;
      }

      const isAlreadyChosen = chosenCollaboratorId === candidateId;
      const nextValue = isAlreadyChosen ? null : candidateId;

      if (nextValue && !slotCandidates.some((candidate) => candidate.collaboratorId === candidateId)) {
        return;
      }

      setValue(`${slotPath}.chosenCollaboratorId`, nextValue, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [chosenCollaboratorId, setValue, slotCandidates, slotPath]
  );

  const handleFulfillmentStrategySelect = useCallback(
    (strategy: 'known' | 'assist' | 'hire') => {
      setValue(`${slotPath}.fulfillmentStrategy`, strategy, {
        shouldDirty: true,
        shouldTouch: true,
      });

      if (strategy === 'hire') {
        if (chosenCollaboratorId) {
          setValue(`${slotPath}.chosenCollaboratorId`, null, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      }
    },
    [chosenCollaboratorId, setValue, slotPath]
  );

  const handleKnownCollaboratorSelect = useCallback(
    (collaborator: CollaboratorPublic | null) => {
      if (!collaborator) {
        if (chosenCollaboratorId) {
          const filtered = slotCandidates.filter((candidate) => candidate.collaboratorId !== chosenCollaboratorId);
          setValue(`${slotPath}.candidates`, filtered, {
            shouldDirty: true,
            shouldTouch: true,
          });
          setValue(`${slotPath}.chosenCollaboratorId`, null, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
        return;
      }

      const updatedCandidate: CandidateWithMatch = {
        collaboratorId: collaborator.id,
        collaboratorName: collaborator.name ?? 'Colaborador sem nome',
        collaboratorAvatar: collaborator.photoUrl ?? null,
        isChosen: true,
        addedAt: new Date(),
      };

      const others = slotCandidates.filter((candidate) => candidate.collaboratorId !== collaborator.id);
      setValue(`${slotPath}.candidates`, [updatedCandidate, ...others], {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(`${slotPath}.chosenCollaboratorId`, collaborator.id, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(`${slotPath}.fulfillmentStrategy`, 'known', {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [chosenCollaboratorId, setValue, slotCandidates, slotPath]
  );

  const toggleDebugVisibility = useCallback((candidateId: string) => {
    setExpandedDebugCandidateId((current) => (current === candidateId ? null : candidateId));
  }, []);

  const handleRemoveSelectedCandidate = useCallback(
    (candidateId: string) => {
      const updated = slotCandidates.filter((candidate) => candidate.collaboratorId !== candidateId);
      setValue(`${slotPath}.candidates`, updated, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setExpandedDebugCandidateId((current) => (current === candidateId ? null : current));
      if (chosenCollaboratorId === candidateId) {
        setValue(`${slotPath}.chosenCollaboratorId`, null, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [chosenCollaboratorId, setValue, slotCandidates, slotPath]
  );

  const handleSegmentFilterToggle = useCallback((key: keyof typeof SEGMENT_FILTER_THRESHOLDS, enabled: boolean) => {
    setSegmentFiltersEnabled((prev) => ({
      ...prev,
      [key]: enabled,
    }));
  }, []);

  const renderSuggestionCard = (suggestion: CandidateWithMatch) => {
    const inputs = suggestion.matchInputs ?? {};

    const totalScore = Math.round(suggestion.matchScore ?? 0);
    const scores = suggestion.matchScores ?? {
      financial: 0,
      availability: 0,
      location: 0,
      capabilities: 0,
    };

    const normalizedSalary =
      suggestion.matchNormalizedSalary ?? (typeof inputs.normalizedSalary === 'number' ? inputs.normalizedSalary : null);
    const limitSalary = typeof inputs.limitSalary === 'number' ? inputs.limitSalary : null;
    const contractType = suggestion.matchContractType ?? (typeof inputs.lastContractType === 'string' ? inputs.lastContractType : null);
    const locationCity = suggestion.matchLocationCity ?? null;
    const locationState = suggestion.matchLocationState ?? null;
    const primaryCapabilityRequirement =
      capabilityValues.find((capability) => capability.isPrimary) ?? capabilityValues[0] ?? null;
    const primaryCapabilityLabel = (() => {
      if (!primaryCapabilityRequirement) {
        return null;
      }
      const techName = primaryCapabilityRequirement.technologyName?.trim();
      if (techName && techName.length > 0) {
        return techName;
      }
      return primaryCapabilityRequirement.typeName?.trim() ?? null;
    })();
    const primaryCapabilityDebug = suggestion.capabilityDebug?.primary ?? null;
    const isPrimaryCapabilitySatisfied =
      primaryCapabilityDebug != null
        ? primaryCapabilityDebug.ratio > 0 && Boolean(primaryCapabilityDebug.matchedCapability)
        : null;
    const primaryCapabilityStatusEmoji =
      isPrimaryCapabilitySatisfied === null
        ? primaryCapabilityLabel
          ? '⚠️'
          : null
        : isPrimaryCapabilitySatisfied
          ? '✅'
          : '⚠️';

    const salaryPercentage =
      normalizedSalary !== null && limitSalary !== null && limitSalary > 0
        ? Math.round((normalizedSalary / limitSalary) * 100)
        : null;
    const SalaryTrendIcon = salaryPercentage !== null && salaryPercentage >= 100 ? ArrowUpRightIcon : ArrowDownRightIcon;
    const salaryTrendColor = salaryPercentage !== null && salaryPercentage > 100 ? 'text-rose-500' : 'text-emerald-600';
    let loadMonthly: number | null = null;
    const rawLoadMonthly = inputs.loadMonthly as unknown;
    if (typeof rawLoadMonthly === 'number' && Number.isFinite(rawLoadMonthly)) {
      loadMonthly = rawLoadMonthly;
    } else if (typeof rawLoadMonthly === 'string') {
      const parsed = Number(rawLoadMonthly);
      if (Number.isFinite(parsed)) {
        loadMonthly = parsed;
      }
    }
    const availableMonthlyHours = loadMonthly !== null ? Math.max(0, MONTHLY_FULL_LOAD - loadMonthly) : MONTHLY_FULL_LOAD;
    const nextAvailabilityDateIso =
      typeof inputs.nextAvailabilityDate === 'string' && inputs.nextAvailabilityDate ? inputs.nextAvailabilityDate : null;
    let nextAvailabilityDate: Date | null = null;
    if (nextAvailabilityDateIso) {
      const parsed = new Date(nextAvailabilityDateIso);
      if (!Number.isNaN(parsed.getTime())) {
        nextAvailabilityDate = parsed;
      }
    }
    const normalizeDate = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const referenceComparisonDate = normalizeDate(referenceDateValue ?? new Date());
    const nextAvailabilityComparison = nextAvailabilityDate ? normalizeDate(nextAvailabilityDate) : null;
    const isFutureAvailability =
      !!nextAvailabilityComparison && nextAvailabilityComparison.getTime() > referenceComparisonDate.getTime();
    const availabilityEmoji = isFutureAvailability ? '⚠️' : '✅';
    let availabilityBadgeLabel: string | null = null;
    if (loadMonthly !== null || nextAvailabilityDate) {
      const parts: string[] = [];
      if (nextAvailabilityComparison && nextAvailabilityDate) {
        if (nextAvailabilityComparison.getTime() > referenceComparisonDate.getTime()) {
          parts.push(`Disponível em ${format(nextAvailabilityDate, 'dd/MM', { locale: ptBR })}`);
        } else if (nextAvailabilityComparison.getTime() < referenceComparisonDate.getTime()) {
          parts.push(`Disponível desde ${format(nextAvailabilityDate, 'dd/MM', { locale: ptBR })}`);
        } else {
          parts.push(`Disponível em ${format(nextAvailabilityDate, 'dd/MM', { locale: ptBR })}`);
        }
      } else {
        parts.push('Disponível agora');
      }
      parts.push(`${availableMonthlyHours}h`);
      availabilityBadgeLabel = parts.join(' • ');
    }

    const cardKey = suggestion.id ?? suggestion.collaboratorId ?? Math.random().toString(36).slice(2);
    const isSelected = Boolean(suggestion.isChosen);
    const showDebug = expandedDebugCandidateId === suggestion.collaboratorId;
    const hasDebugData = Boolean(suggestion.capabilityDebug || suggestion.debug);

    return (
      <div
        key={cardKey}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        onClick={() => handleToggleCandidateSelection(suggestion)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggleCandidateSelection(suggestion);
          }
        }}
        className={clsx(
          'rounded-lg border p-4 shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400',
          isSelected
            ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
            : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow'
        )}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:items-start">
            <div className="flex flex-col gap-2">
              <div>
                <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1">
                  <span className="text-[10px] font-medium uppercase text-indigo-600">Score</span>
                  <span className="text-base font-semibold text-indigo-700">
                    {Number.isFinite(totalScore) ? totalScore : '--'}
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                {suggestion.collaboratorAvatar ? (
                  <img
                    src={suggestion.collaboratorAvatar}
                    alt={suggestion.collaboratorName ?? 'Colaborador sem nome'}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-600">
                    {(suggestion.collaboratorName ?? '??').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <p className="text-[13px] font-semibold text-gray-900">
                  {suggestion.collaboratorName ?? 'Colaborador sem nome'}
                </p>
              </div>
              {hasDebugData ? (
                <div>
                  <button
                    type="button"
                    className="text-[11px] font-medium text-indigo-600 hover:text-indigo-500"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleDebugVisibility(suggestion.collaboratorId);
                    }}
                  >
                    {showDebug ? 'Ocultar debug' : 'Mostrar debug'}
                  </button>
                </div>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 md:justify-start">
              <div className="flex min-h-[42px] flex-col gap-1">
                <ScoreLine segmentKey="availability" score={scores.availability} />
                <div className="flex justify-start">
                  {availabilityBadgeLabel ? (
                    <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-600">
                      <span>{availabilityEmoji}</span>
                      <span>{availabilityBadgeLabel}</span>
                    </span>
                  ) : (
                    <span className="text-[11px] font-medium text-gray-500">Sem dados de disponibilidade</span>
                  )}
                </div>
              </div>
              <div className="flex min-h-[42px] flex-col gap-1">
                <ScoreLine segmentKey="financial" score={scores.financial} />
                {normalizedSalary !== null ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-[11px] font-semibold text-gray-600">{currencyFormatter.format(normalizedSalary)}</p>
                    {contractType ? (
                      <span className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                        {contractType.toUpperCase()}
                      </span>
                    ) : null}
                    {salaryPercentage !== null ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                        <SalaryTrendIcon className={`h-3 w-3 ${salaryTrendColor}`} />
                        {salaryPercentage}% do alvo
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <span className="text-[11px] font-medium text-gray-500">Salário não informado</span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 md:justify-start">
              <div className="flex min-h-[42px] flex-col gap-1">
                <ScoreLine segmentKey="location" score={scores.location} />
                <span className="text-[11px] font-medium text-gray-600">
                  {[locationCity, locationState].filter(Boolean).join(' - ') || 'Localização não informada'}
                </span>
              </div>
              <div className="flex min-h-[42px] flex-col gap-1">
                <ScoreLine segmentKey="capabilities" score={scores.capabilities} />
                {primaryCapabilityStatusEmoji ? (
                  <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-600">
                    <span>{primaryCapabilityStatusEmoji}</span>
                    <span>{primaryCapabilityLabel ?? 'Capacidade principal'}</span>
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {showDebug ? (
            <div className="space-y-3 border-t border-gray-100 pt-3 text-[11px] text-gray-700">
              {suggestion.capabilityDebug ? (
                <details open className="rounded border border-dashed border-gray-200 bg-gray-50 p-2">
                  <summary className="cursor-pointer font-semibold text-gray-700">
                    Debug capacidades (temporário)
                  </summary>
                  <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap break-words text-gray-700">
                    {JSON.stringify(suggestion.capabilityDebug, null, 2)}
                  </pre>
                </details>
              ) : null}
              {suggestion.debug ? (
                <details open className="rounded border border-dashed border-gray-200 bg-gray-50 p-2">
                  <summary className="cursor-pointer font-semibold text-gray-700">
                    Debug completo (temporário)
                  </summary>
                  <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap break-words text-gray-700">
                    {JSON.stringify(suggestion.debug, null, 2)}
                  </pre>
                </details>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const capabilitiesArray = useFieldArray({
    control,
    name: `${slotPath}.capabilities` as const,
  });

  const capabilityValues =
    (watch(`${slotPath}.capabilities`) as AvailabilityCapability[] | undefined) ?? [];

  const shouldShowWorkLocation =
    slot?.workModel === AvailabilityWorkModels.Onsite ||
    slot?.workModel === AvailabilityWorkModels.Hybrid;
  const workLocationError =
    (slotErrors?.workCity?.message as string | undefined) ||
    (slotErrors?.workState?.message as string | undefined) ||
    (slotErrors?.workCity as string | undefined) ||
    (slotErrors?.workState as string | undefined);
  const {
    searchTerm: capabilitySearchTerm,
    setSearchTerm: setCapabilitySearchTerm,
    isSearching: isCapabilitySearching,
    visibleTypes: visibleCapabilityTypes,
    visibleTechnologies: visibleCapabilityTechnologies,
  } = useCapabilitySearch({
    types,
    technologies,
    selectedTypeId: capabilitySelection.typeId,
  });

  useEffect(() => {
    if (!visibleCapabilityTypes.length) {
      setCapabilitySelection((prev) => ({
        ...prev,
        typeId: '',
        typeName: '',
        technologyId: '',
        technologyName: '',
      }));
      return;
    }

    setCapabilitySelection((prev) => {
      if (prev.typeId && visibleCapabilityTypes.some((type) => type.id === prev.typeId)) {
        return prev;
      }

      const nextType = visibleCapabilityTypes[0];
      if (!nextType) {
        return prev;
      }

      return {
        ...prev,
        typeId: nextType.id,
        typeName: nextType.name,
        technologyId: '',
        technologyName: '',
      };
    });
  }, [visibleCapabilityTypes, setCapabilitySelection]);

  useEffect(() => {
    if (
      capabilitySelection.technologyId &&
      !visibleCapabilityTechnologies.some((tech) => tech.id === capabilitySelection.technologyId)
    ) {
      setCapabilitySelection((prev) => ({
        ...prev,
        technologyId: '',
        technologyName: '',
      }));
    }
  }, [visibleCapabilityTechnologies, capabilitySelection.technologyId, setCapabilitySelection]);

  const selectedType = useMemo(
    () => types.find((type) => type.id === capabilitySelection.typeId) ?? null,
    [types, capabilitySelection.typeId]
  );

  const selectedTechnology = useMemo(
    () =>
      visibleCapabilityTechnologies.find((tech) => tech.id === capabilitySelection.technologyId) ?? null,
    [visibleCapabilityTechnologies, capabilitySelection.technologyId]
  );

  const selectedSeniority = useMemo(
    () => seniorities.find((seniority) => seniority.id === capabilitySelection.seniorityId) ?? null,
    [seniorities, capabilitySelection.seniorityId]
  );

  const resetCapabilitySelection = () => {
    setCapabilitySelection({
      typeId: '',
      typeName: '',
      technologyId: '',
      technologyName: '',
      seniorityId: '',
      seniorityName: '',
      required: true,
    });
  };

  const handleSelectType = (type: ResourceType) => {
    setCapabilitySelection((prev) => {
      const isSameType = prev.typeId === type.id;
      if (isSameType) {
        return {
          typeId: '',
          typeName: '',
          technologyId: '',
          technologyName: '',
          seniorityId: '',
          seniorityName: '',
          required: prev.required,
        };
      }

      return {
        typeId: type.id,
        typeName: type.name,
        technologyId: '',
        technologyName: '',
        seniorityId: '',
        seniorityName: '',
        required: prev.required,
      };
    });
    setCapabilityError(null);
  };

  const handleSelectTechnology = (technology: Technology) => {
    setCapabilitySelection((prev) => ({
      ...prev,
      typeId: technology.resourceTypeId ?? prev.typeId,
      typeName:
        technology.resourceTypeId && types
          ? types.find((type) => type.id === technology.resourceTypeId)?.name ?? prev.typeName
          : prev.typeName,
      technologyId: prev.technologyId === technology.id ? '' : technology.id,
      technologyName: prev.technologyId === technology.id ? '' : technology.name,
    }));
  };

  const handleSelectSeniority = (seniority: Seniority) => {
    setCapabilitySelection((prev) => ({
      ...prev,
      seniorityId: prev.seniorityId === seniority.id ? '' : seniority.id,
      seniorityName: prev.seniorityId === seniority.id ? '' : seniority.name,
    }));
  };

  const handleToggleRequired = (value: boolean) => {
    setCapabilitySelection((prev) => ({
      ...prev,
      required: value,
    }));
  };

  const handleSuggestCollaborators = async () => {
    try {
      const referenceDate = getValues('referenceDate');
      setSuggestionMeta(null);
      setCollaboratorSearchTerm('');
      const segmentCutoffsPayload = SEGMENT_FILTER_ORDER.reduce(
        (acc, key) => {
          if (segmentFiltersEnabled[key]) {
            acc[key] = SEGMENT_FILTER_THRESHOLDS[key];
          }
          return acc;
        },
        {} as Partial<Record<keyof typeof SEGMENT_FILTER_THRESHOLDS, number>>
      );
      const normalizedFilters = {
        scoreCutoff,
        segmentFilters: segmentCutoffsPayload,
        searchTerm: collaboratorSearchTerm || null,
        referenceDate:
          referenceDate instanceof Date ? referenceDate.toISOString() : referenceDate ?? null,
        workModel: slot?.workModel ?? null,
        workState: slot?.workState ?? null,
        acceptsPJ: typeof slot?.acceptsPJ === 'boolean' ? slot?.acceptsPJ : null,
        cltSalaryCeiling: slot?.cltSalaryCeiling ?? null,
        capabilities: (slot?.capabilities ?? []).map((capability) => ({
          typeId: capability.typeId,
          technologyId: capability.technologyId ?? null,
          seniorityId: capability.seniorityId ?? null,
          required: capability.required,
          isPrimary: capability.isPrimary ?? false,
        })),
      };

      const payload = {
        availabilityId,
        slotId,
        scoreCutoff,
        segmentCutoffs: segmentCutoffsPayload,
        slot: {
          availabilityId,
          slotId,
          title: slot?.title ?? '',
          workModel: slot?.workModel,
          workState: slot?.workState,
          acceptsPJ: slot?.acceptsPJ,
          salaryLimit: slot?.cltSalaryCeiling,
          referenceDate: referenceDate instanceof Date ? referenceDate.toISOString() : referenceDate ?? null,
          capabilities: (slot?.capabilities ?? []).map((capability) => ({
            capabilityId: capability.typeId,
            typeId: capability.typeId,
            technologyId: capability.technologyId,
            seniorityId: capability.seniorityId,
            isPrimary: capability.isPrimary ?? false,
            required: capability.required,
          })),
        },
      };

      const response = await runMatchMutation.mutateAsync(payload);

      const now = new Date();
      const previouslySelectedIds = new Set(
        slotCandidates.filter((candidate) => candidate.isChosen).map((candidate) => candidate.collaboratorId)
      );
      const mappedCandidates: CandidateWithMatch[] = response.suggestions.map((suggestion) => ({
        id: suggestion.collaboratorId,
        collaboratorId: suggestion.collaboratorId,
        collaboratorName: suggestion.fullName,
        collaboratorAvatar: suggestion.avatarUrl ?? null,
        isChosen: previouslySelectedIds.has(suggestion.collaboratorId),
        addedAt: now,
        matchScore: suggestion.scoreTotal,
        matchScores: suggestion.scores,
        matchAssumptions: suggestion.assumptions,
        matchInputs: suggestion.inputs,
        matchContractType: suggestion.contract?.lastType ?? null,
        matchNormalizedSalary: suggestion.contract?.normalizedSalary ?? null,
        matchLocationCity: suggestion.location?.city ?? null,
        matchLocationState: suggestion.location?.state ?? null,
        capabilityDebug: suggestion.capabilityDebug,
        debug: suggestion.debug,
      }));

      setSuggestionResults(mappedCandidates);

      const updatedSelected = slotCandidates
        .map((candidate) => {
          const latest = mappedCandidates.find((suggestion) => suggestion.collaboratorId === candidate.collaboratorId);
          return latest ? { ...latest, isChosen: true } : candidate;
        })
        .filter((candidate) => candidate.collaboratorId);

      setValue(`${slotPath}.candidates`, updatedSelected, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(`${slotPath}.lastSuggestionAt`, now, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(`${slotPath}.lastMatchFilters`, normalizedFilters, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(`${slotPath}.lastMatchResultsCount`, response.total ?? mappedCandidates.length ?? 0, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setExpandedDebugCandidateId(null);
      setSuggestionMeta({
        total: response.total,
        page: response.suggestions.length > 0 ? 1 : 0,
        pageSize: response.suggestions.length,
        totalPages: response.suggestions.length > 0 ? 1 : 0,
      });

      setLastUpdatedAt(now);

      if (mappedCandidates.length === 0) {
        info('Nenhuma sugestão encontrada', 'Ajuste os requisitos para ampliar as opções disponíveis.');
      } else {
        success('Sugestões atualizadas', 'Selecione os colaboradores desejados para adicionar à vaga.');
      }
    } catch (err) {
      setSuggestionMeta(null);
      const message = err instanceof Error ? err.message : 'Não foi possível gerar as sugestões.';
      error('Falha ao gerar sugestões', message);
    }
  };

  const handleAddCapability = () => {
    if (!capabilitySelection.typeId) {
      setCapabilityError('Selecione um tipo de capacidade para adicionar.');
      return;
    }

    const typeName = capabilitySelection.typeName || selectedType?.name || 'Tipo não identificado';
    const technologyName =
      capabilitySelection.technologyName || selectedTechnology?.name || null;
    const seniorityName =
      capabilitySelection.seniorityName || selectedSeniority?.name || null;

    const existingCapabilities =
      (watch(`${slotPath}.capabilities`) as AvailabilityCapability[] | undefined) ?? [];
    const hasPrimaryCapability = existingCapabilities.some((cap) => cap?.isPrimary);

    capabilitiesArray.append({
      typeId: capabilitySelection.typeId,
      typeName,
      technologyId: capabilitySelection.technologyId ? capabilitySelection.technologyId : null,
      technologyName,
      seniorityId: capabilitySelection.seniorityId ? capabilitySelection.seniorityId : null,
      seniorityName,
      required: capabilitySelection.required,
      isPrimary: !hasPrimaryCapability,
    });

    resetCapabilitySelection();
    setCapabilityError(null);
    info('Capacidade adicionada', `${typeName}${technologyName ? ` • ${technologyName}` : ''}`);
  };

  const handleSetPrimaryCapability = (index: number) => {
    capabilityValues.forEach((_, capabilityIndex) => {
      setValue(
        `${slotPath}.capabilities.${capabilityIndex}.isPrimary`,
        capabilityIndex === index,
        {
          shouldDirty: true,
          shouldTouch: true,
        }
      );
    });
  };

  const handleRemoveCapability = (index: number) => {
    capabilitiesArray.remove(index);
    const removedCapability = capabilityValues?.[index];
    setTimeout(() => {
      const remainingCapabilities =
        (watch(`${slotPath}.capabilities`) as AvailabilityCapability[] | undefined) ?? [];
      const hasPrimary = remainingCapabilities.some((cap) => cap?.isPrimary);
      if (!hasPrimary && remainingCapabilities.length > 0) {
        setValue(`${slotPath}.capabilities.0.isPrimary`, true, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }, 0);
    if (removedCapability?.typeName) {
      info(
        'Capacidade removida',
        `${removedCapability.typeName}${removedCapability.technologyName ? ` • ${removedCapability.technologyName}` : ''
        }`
      );
    }
  };

  if (slotIndex === null) {
    return null;
  }



  const handleWorkModelChange = (value: AvailabilityWorkModel) => {
    setValue(`${slotPath}.workModel`, value, {
      shouldDirty: true,
      shouldTouch: true,
    });
    if (value === AvailabilityWorkModels.Remote) {
      setValue(`${slotPath}.workCity`, null, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(`${slotPath}.workState`, null, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  const handleBucketTypeChange = (value: AllocationBucketType) => {
    setValue(`${slotPath}.bucketType`, value, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const slotTitle = slot?.title?.trim() ? slot.title : `Vaga ${resolvedSlotIndex + 1}`;
  const dedicationBuckets: AllocationBucketType[] = [
    AllocationBucketTypes.Monthly,
    AllocationBucketTypes.Defined,
    AllocationBucketTypes.Daily,
    AllocationBucketTypes.Weekly,
  ];

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative flex w-full max-w-4xl transform flex-col overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all">
                <header className="shrink-0 border-b border-gray-200 px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">{slotTitle}</Dialog.Title>
                  <p className="mt-1 text-sm text-gray-500">
                    Configure os detalhes da vaga, incluindo perfil desejado e restrições de contratação.
                  </p>
                </header>
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  <div className="mb-6 flex gap-4 border-b border-gray-200">
                    {TAB_OPTIONS.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={clsx(
                          'pb-2 text-sm font-medium transition-colors',
                          activeTab === tab.key
                            ? 'border-b-2 border-indigo-500 text-indigo-600'
                            : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700'
                        )}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {activeTab === 'vaga' && (
                    <div className="space-y-6">
                      <section className="grid gap-4 md:grid-cols-[2fr_1fr_auto_auto_auto]">
                        <div>
                          <label className="text-sm font-medium text-gray-700" htmlFor={`slot-${resolvedSlotIndex}-title`}>
                            Título da vaga
                          </label>
                          <input
                            id={`slot-${resolvedSlotIndex}-title`}
                            type="text"
                            className="input-primary mt-1 w-full"
                            placeholder="Ex: Frontend Pleno"
                            {...register(`${slotPath}.title` as const)}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700" htmlFor={`slot-${resolvedSlotIndex}-salary`}>
                            Limite salarial (CLT)
                          </label>
                          <input
                            id={`slot-${resolvedSlotIndex}-salary`}
                            type="number"
                            min={0}
                            className="input-primary mt-1 w-full"
                            placeholder="Valor em reais"
                            {...register(`${slotPath}.cltSalaryCeiling` as const, {
                              valueAsNumber: true,
                            })}
                          />
                        </div>
                        <div>
                          <label
                            className="text-sm font-medium text-gray-700"
                            htmlFor={`slot-${resolvedSlotIndex}-accepts-pj`}
                          >
                            Aceita contratação PJ
                          </label>
                          <div className="mt-1 flex items-center gap-2">
                            <input
                              id={`slot-${resolvedSlotIndex}-accepts-pj`}
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              {...register(`${slotPath}.acceptsPJ` as const)}
                            />
                            <span className="text-sm text-gray-700">Sim</span>
                          </div>
                        </div>
                      </section>

                      <section className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Modelo de trabalho</label>
                          <div className="mt-2 grid gap-2 sm:grid-cols-3">
                            {(Object.keys(AvailabilityWorkModels) as AvailabilityWorkModel[]).map((workModel) => {
                              const value = AvailabilityWorkModels[workModel];
                              const meta = AVAILABILITY_WORK_MODEL_METADATA[value];
                              const isSelected = slot?.workModel === value;
                              const Icon = WORK_MODEL_ICON_MAP[value];
                              return (
                                <button
                                  key={value}
                                  type="button"
                                  className={clsx(
                                    'flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                                    isSelected
                                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm'
                                      : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                                  )}
                                  onClick={() => handleWorkModelChange(value)}
                                >
                                  <span
                                    className={clsx(
                                      'rounded-md p-2',
                                      isSelected ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-indigo-500'
                                    )}
                                  >
                                    <Icon className="h-4 w-4" />
                                  </span>
                                  <span className="font-medium">{meta.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {shouldShowWorkLocation ? (
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700" htmlFor={`slot-${resolvedSlotIndex}-work-city`}>
                              Cidade da atuação <span className="text-red-500">*</span>
                            </label>
                            <GooglePlacesControlledInput
                              name={`${slotPath}.workCity`}
                              control={control}
                              placeholder="Informe a cidade da atuação"
                              className="input-primary mt-1 w-full"
                              types={['(cities)']}
                              onSelectAddress={(address) => {
                                const city = address.city || address.fullAddress || '';
                                const state = (address.state || '').toUpperCase();
                                setValue(`${slotPath}.workCity`, city || '', {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                });
                                setValue(`${slotPath}.workState`, state || '', {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                });
                              }}
                            />
                            {workLocationError ? (
                              <p className="text-xs text-red-600">{workLocationError}</p>
                            ) : null}
                          </div>
                        ) : null}
                      </section>

                      <section className="space-y-4">
                        <label className="text-sm font-medium text-gray-700">Dedicação necessária</label>
                        <div className="flex w-full flex-wrap items-stretch gap-3 md:flex-nowrap">
                          {dedicationBuckets.map((value) => {
                            const meta = bucketMetadata[value];
                            const isSelected = currentBucketType === value;
                            const Icon = ALLOCATION_BUCKET_ICON_MAP[value];
                            return (
                              <button
                                key={value}
                                type="button"
                                className={clsx(
                                  'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors whitespace-nowrap md:flex-1 md:basis-0',
                                  isSelected
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm'
                                    : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                                )}
                                onClick={() => handleBucketTypeChange(value)}
                              >
                                <span
                                  className={clsx(
                                    'rounded-md p-2',
                                    isSelected ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-indigo-500'
                                  )}
                                >
                                  <Icon className="h-4 w-4" />
                                </span>
                                <span
                                  className={clsx(
                                    'font-semibold',
                                    isSelected ? 'text-indigo-700' : 'text-gray-700'
                                  )}
                                >
                                  {meta?.label}
                                </span>
                              </button>
                            );
                          })}
                          <div className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 whitespace-nowrap md:flex-1 md:basis-0">
                            <label
                              className="text-sm font-semibold text-gray-900"
                              htmlFor={`slot-${resolvedSlotIndex}-bucket`}
                            >
                              Horas
                            </label>
                            <input
                              id={`slot-${resolvedSlotIndex}-bucket`}
                              type="number"
                              min={0}
                              step="0.5"
                              className="h-9 w-20 rounded border border-gray-300 px-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              {...register(`${slotPath}.bucketValue` as const, {
                                valueAsNumber: true,
                              })}
                            />
                          </div>
                        </div>
                      </section>
                    </div>
                  )}

                  {activeTab === 'habilidades' && (
                    <div className="space-y-6">
                      <section className="space-y-6">
                        <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="space-y-1">
                            <h4 className="text-sm font-semibold text-gray-900">Capacidades</h4>
                            <p className="text-xs text-gray-500">
                              Selecione o tipo, tecnologia e senioridade desejada para adicionar novas capacidades a esta vaga.
                            </p>
                          </div>
                          <CapabilitySearchInput
                            value={capabilitySearchTerm}
                            onChange={setCapabilitySearchTerm}
                            className="sm:w-72"
                          />
                        </header>

                        <div className="grid gap-4 lg:grid-cols-2">
                          <ResourceTypeList
                            types={visibleCapabilityTypes}
                            selectedTypeId={capabilitySelection.typeId}
                            onSelect={handleSelectType}
                            loading={resourcesLoading}
                            error={resourcesError}
                            disabled={resourcesLoading}
                            emptyMessage={
                              isCapabilitySearching
                                ? 'Nenhum tipo corresponde à sua busca.'
                                : 'Nenhum tipo de capacidade cadastrado.'
                            }
                          />

                          {capabilitySelection.typeId ? (
                            <TechnologyList
                              technologies={visibleCapabilityTechnologies}
                              types={types}
                              selectedTechnologyId={capabilitySelection.technologyId}
                              onSelect={handleSelectTechnology}
                              loading={resourcesLoading}
                              error={resourcesError}
                              disabled={resourcesLoading}
                              isSearching={isCapabilitySearching}
                            />
                          ) : (
                            <div className="flex min-h-[200px] items-center justify-center rounded-md border border-dashed border-gray-200 bg-white p-4 text-center text-sm text-gray-500">
                              Selecione um tipo de capacidade para visualizar tecnologias relacionadas.
                            </div>
                          )}
                        </div>

                        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
                          <div>
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Senioridade (opcional)
                            </span>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {seniorities.map((seniority) => {
                                const isSelected = capabilitySelection.seniorityId === seniority.id;
                                return (
                                  <button
                                    key={seniority.id}
                                    type="button"
                                    onClick={() => handleSelectSeniority(seniority)}
                                    className={clsx(
                                      'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                                      isSelected
                                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    )}
                                  >
                                    {seniority.name}
                                  </button>
                                );
                              })}
                              {capabilitySelection.seniorityId ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCapabilitySelection((prev) => ({
                                      ...prev,
                                      seniorityId: '',
                                      seniorityName: '',
                                    }))
                                  }
                                  className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                                >
                                  Limpar
                                </button>
                              ) : null}
                            </div>
                          </div>

                          <div className="space-y-3">
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                checked={capabilitySelection.required}
                                onChange={(event) => handleToggleRequired(event.target.checked)}
                              />
                              Capacidade obrigatória
                            </label>

                            <button
                              type="button"
                              className="btn-primary w-full"
                              onClick={handleAddCapability}
                              disabled={!capabilitySelection.typeId || resourcesLoading}
                            >
                              Adicionar capacidade
                            </button>

                            {capabilityError ? (
                              <p className="text-xs text-red-600">{capabilityError}</p>
                            ) : null}

                            {selectedType ? (
                              <p className="text-xs text-gray-500">
                                Pronto para adicionar: <strong>{selectedType.name}</strong>
                                {selectedTechnology ? ` • ${selectedTechnology.name}` : ''}
                                {selectedSeniority ? ` • ${selectedSeniority.name}` : ''}
                                {' '}
                                ({capabilitySelection.required ? 'obrigatória' : 'desejável'}).
                              </p>
                            ) : (
                              <p className="text-xs text-gray-400">
                                Selecione ao menos um tipo de capacidade para adicionar.
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3">
                          {capabilityValues.length === 0 ? (
                            <p className="text-sm text-gray-500">Nenhuma capacidade adicionada até o momento.</p>
                          ) : null}
                          {capabilityValues.map((capabilityValue, capabilityIndex) => {
                            const field = capabilitiesArray.fields[capabilityIndex];
                            const fieldKey =
                              field?.id ??
                              `${capabilityValue?.typeId ?? 'cap'}-${capabilityIndex}`;
                            const seniorityDisplayName =
                              capabilityValue?.seniorityName ||
                              seniorities.find((seniority) => seniority.id === capabilityValue?.seniorityId)?.name ||
                              null;
                            const capabilityLabelParts = [
                              capabilityValue?.typeName,
                              capabilityValue?.technologyName,
                              seniorityDisplayName,
                            ].filter(Boolean);
                            const capabilityLabel = capabilityLabelParts.length
                              ? capabilityLabelParts.join(' • ')
                              : 'Capacidade sem identificação';
                            const isRequiredValue = capabilityValue?.required ?? false;

                            return (
                              <div
                                key={fieldKey}
                                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                              >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="flex items-start gap-3">
                                    <button
                                      type="button"
                                      onClick={() => handleSetPrimaryCapability(capabilityIndex)}
                                      className={clsx(
                                        'flex h-8 w-8 items-center justify-center rounded-full border transition-colors',
                                        capabilityValue?.isPrimary
                                          ? 'border-amber-300 bg-amber-50 text-amber-500'
                                          : 'border-gray-200 bg-white text-gray-400 hover:border-amber-200 hover:text-amber-400'
                                      )}
                                      title={
                                        capabilityValue?.isPrimary
                                          ? 'Capacidade principal'
                                          : 'Marcar como capacidade principal'
                                      }
                                    >
                                      {capabilityValue?.isPrimary ? (
                                        <StarSolidIcon className="h-4 w-4" />
                                      ) : (
                                        <StarOutlineIcon className="h-4 w-4" />
                                      )}
                                    </button>
                                    <div>
                                      <p className="text-sm font-semibold text-gray-900">
                                        {capabilityLabel}
                                      </p>
                                      <div className="mt-1 flex flex-wrap items-center gap-2">
                                        <span
                                          className={clsx(
                                            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                                            isRequiredValue
                                              ? 'bg-indigo-100 text-indigo-700'
                                              : 'bg-gray-100 text-gray-600'
                                          )}
                                        >
                                          {isRequiredValue ? 'Obrigatória' : 'Desejável'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        {...register(`${slotPath}.capabilities.${capabilityIndex}.required` as const)}
                                      />
                                      Obrigatória
                                    </label>
                                    <button
                                      type="button"
                                      className="text-xs font-medium text-red-600 hover:text-red-500"
                                      onClick={() => handleRemoveCapability(capabilityIndex)}
                                    >
                                      Remover
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    </div>
                  )}

                  {activeTab === 'colaboradores' && (
                    <div className="space-y-6">
                      <section className="space-y-3">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Como deseja preencher a vaga?</h4>
                          <p className="text-xs text-gray-500">Selecione a estratégia para avançar com esta necessidade.</p>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-3">
                          {FULFILLMENT_STRATEGY_OPTIONS.map((option) => {
                            const Icon = option.icon;
                            const selected = fulfillmentStrategy === option.key;
                            return (
                              <label
                                key={option.key}
                                className={clsx(
                                  'relative flex cursor-pointer flex-col gap-2 rounded-lg border px-4 py-4 text-left shadow-sm transition-all focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2',
                                  selected
                                    ? 'border-indigo-500 bg-indigo-50'
                                    : 'border-gray-200 bg-white hover:border-indigo-300'
                                )}
                              >
                                <input
                                  type="radio"
                                  name={`fulfillment-${slotId}`}
                                  value={option.key}
                                  className="sr-only"
                                  checked={selected}
                                  onChange={() => handleFulfillmentStrategySelect(option.key)}
                                />
                                <div className="flex items-center gap-3">
                                  <span
                                    className={clsx(
                                      'flex h-12 w-12 items-center justify-center rounded-full text-indigo-600',
                                      selected ? 'bg-white' : 'bg-indigo-50'
                                    )}
                                  >
                                    <Icon className="h-5 w-5" />
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">{option.label}</span>
                                </div>
                                <p className="text-xs text-gray-500">{option.description}</p>
                              </label>
                            );
                          })}
                        </div>
                      </section>

                      {fulfillmentStrategy === 'known' ? (
                        <section className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Selecione o colaborador</label>
                            <CollaboratorCombobox
                              selectedId={chosenCollaboratorId ?? undefined}
                              onChange={handleKnownCollaboratorSelect}
                              placeholder="Buscar colaborador"
                            />
                          </div>
                          {chosenCandidate ? (
                            <div className="flex items-center gap-3 rounded-lg border border-indigo-100 bg-indigo-50 p-4">
                              {chosenCandidate.collaboratorAvatar ? (
                                <img
                                  src={chosenCandidate.collaboratorAvatar}
                                  alt={chosenCandidate.collaboratorName ?? 'Colaborador escolhido'}
                                  className="h-10 w-10 rounded-full object-cover shadow"
                                />
                              ) : (
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-600">
                                  {(chosenCandidate.collaboratorName ?? '??').slice(0, 2).toUpperCase()}
                                </span>
                              )}
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold text-gray-900">
                                  {chosenCandidate.collaboratorName ?? 'Colaborador sem nome'}
                                </span>
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">
                                  <StarSolidIcon className="h-4 w-4" /> Escolhido
                                </span>
                              </div>
                              <button
                                type="button"
                                className="ml-auto text-xs font-medium text-indigo-600 hover:text-indigo-500"
                                onClick={() => handleKnownCollaboratorSelect(null)}
                              >
                                Remover seleção
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">Escolha um colaborador para vincular diretamente a esta vaga.</p>
                          )}
                        </section>
                      ) : null}

                      {fulfillmentStrategy === 'assist' ? (
                        <>
                          {selectedCandidatesList.length > 0 ? (
                            <div className="space-y-2 rounded-lg border border-indigo-100 bg-indigo-50/70 p-3">
                              <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                                <span>Selecionados</span>
                                <span>{selectedCandidatesList.length}</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {selectedCandidatesList.map((candidate) => {
                                  const isChosenCollaborator = candidate.collaboratorId === chosenCollaboratorId;

                                  return (
                                    <div
                                      key={`selected-${candidate.collaboratorId}`}
                                      className={clsx(
                                        'inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-xs shadow-sm ring-1 transition-colors',
                                        isChosenCollaborator
                                          ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                                          : 'bg-white text-gray-700 ring-indigo-100'
                                      )}
                                    >
                                      <button
                                        type="button"
                                        className={clsx(
                                          'flex h-6 w-6 items-center justify-center rounded-full border text-indigo-500 transition-colors',
                                          isChosenCollaborator
                                            ? 'border-indigo-300 bg-indigo-100'
                                            : 'border-transparent hover:border-indigo-200 hover:bg-indigo-50'
                                        )}
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          handleToggleChosenCandidate(candidate.collaboratorId);
                                        }}
                                        title={
                                          isChosenCollaborator
                                            ? 'Desmarcar colaborador escolhido'
                                            : 'Marcar como colaborador escolhido'
                                        }
                                      >
                                        {isChosenCollaborator ? (
                                          <StarSolidIcon className="h-4 w-4" />
                                        ) : (
                                          <StarOutlineIcon className="h-4 w-4" />
                                        )}
                                      </button>

                                      <span className="font-semibold text-current">
                                        {candidate.collaboratorName ?? 'Colaborador sem nome'}
                                      </span>

                                      {isChosenCollaborator ? (
                                        <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">
                                          Escolhido
                                        </span>
                                      ) : null}

                                      <button
                                        type="button"
                                        className="text-indigo-600 hover:text-indigo-500"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          handleRemoveSelectedCandidate(candidate.collaboratorId);
                                        }}
                                      >
                                        <span className="sr-only">
                                          Remover {candidate.collaboratorName ?? 'colaborador'} da seleção
                                        </span>
                                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null}

                          <section className="space-y-4">
                            <header className="space-y-3">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900">Colaboradores sugeridos</h4>
                              </div>
                              <div className="grid gap-3 lg:grid-cols-[220px_1fr_220px]">
                                <div className="flex w-full flex-col gap-1">
                                  <div className="flex items-center justify-between text-[11px] font-medium text-gray-500">
                                    <span>Nota de corte</span>
                                    <span>{scoreCutoff}</span>
                                  </div>
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={5}
                                    value={scoreCutoff}
                                    onChange={(event) => setScoreCutoff(Number(event.target.value))}
                                    className="accent-indigo-600"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  {SEGMENT_FILTER_ORDER.map((key) => {
                                    const style = SEGMENT_FILTER_STYLES[key];
                                    return (
                                      <label
                                        key={key}
                                        className={clsx(
                                          'inline-flex w-full items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                                          segmentFiltersEnabled[key] ? style.active : style.inactive
                                        )}
                                      >
                                        <input
                                          type="checkbox"
                                          className={clsx('h-4 w-4 rounded border-transparent focus:ring-offset-0', style.toggle)}
                                          checked={segmentFiltersEnabled[key]}
                                          onChange={(event) => handleSegmentFilterToggle(key, event.target.checked)}
                                        />
                                        {SEGMENT_FILTER_LABELS[key]}
                                      </label>
                                    );
                                  })}
                                </div>
                                <div className="flex flex-col gap-2">
                                  <input
                                    type="text"
                                    value={collaboratorSearchTerm}
                                    onChange={(event) => setCollaboratorSearchTerm(event.target.value)}
                                    placeholder="Nome do Colaborador"
                                    className="input-primary"
                                  />
                                  <button
                                    type="button"
                                    className="btn-primary inline-flex items-center justify-center gap-2 text-sm"
                                    onClick={handleSuggestCollaborators}
                                    disabled={isSuggestDisabled}
                                  >
                                    {isSuggesting ? 'Gerando...' : 'Buscar'}
                                    {!isSuggesting && totalSuggestionsCount > 0 ? (
                                      <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white">
                                        {totalSuggestionsCount}
                                      </span>
                                    ) : null}
                                  </button>
                                </div>
                              </div>
                            </header>

                            {isSuggestionsBusy ? (
                              <div className="animate-pulse rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                                Calculando sugestões de colaboradores...
                              </div>
                            ) : filteredCandidates.length > 0 ? (
                              <div className="space-y-3">
                                {filteredCandidates.map((suggestion) => renderSuggestionCard(suggestion))}
                              </div>
                            ) : (
                              <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                                Ainda não há sugestões para esta vaga. Gere recomendações para visualizar colaboradores alinhados ao perfil desejado.
                              </div>
                            )}
                          </section>
                        </>
                      ) : null}

                      {fulfillmentStrategy === 'hire' ? (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                          Nenhuma ação adicional é necessária aqui. A equipe seguirá com a contratação externa para preencher esta vaga.
                        </div>
                      ) : null}
                    </div>
                  )}

                </div>

                <footer className="flex flex-col justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={onClose}
                  >
                    Fechar
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={onClose}
                  >
                    Concluir ajustes
                  </button>
                </footer>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
